// @flow
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';

import style from './Alert.scss';
import icons from '../../../assets/styles/icons.scss';

type Props = {
  // This will be used to identify the alert and close it
  +id: string;
  +alertType?: 'success' | 'error';
  +open?: boolean;
  +message?: string;
  +messageIntl?: string;
  +unsetAlert: (id: string) => void;
  +closeAlert: (id: string) => void;
  // This will be used in the place where AlertContainer is implemented
  // in order to only show the one with the same id
  +showId: string | string[];
};

// Units in ms
const CLOSE_TIME = 5000;
const UNSET_TIME = 600;

export default class Alert extends Component<Props, void> {
  closeTimer: ?TimeoutID;
  unsetTimer: ?TimeoutID;

  componentDidUpdate(prevProps: Props) {
    const { id, open, alertType, unsetAlert, closeAlert } = this.props;
    // If the alert has been open or closed
    if (open !== prevProps.open) {
      // if the alert has been open and is a success alert set a timer to close it
      if (open && alertType === 'success') {
        this.closeTimer = setTimeout(() => {
          this.closeTimer = undefined;
          closeAlert(id || '')
        }, CLOSE_TIME);
        // if the alert has been close, clear any previous unset timers and set a new one
      } else if (!open) {
        if (typeof this.unsetTimer !== 'undefined' && this.unsetTimer !== null) {
          clearTimeout(this.unsetTimer);
        }
        this.unsetTimer = setTimeout(() => {
          this.unsetTimer = undefined;
          unsetAlert(id || '');
        }, UNSET_TIME);
      }
      // if the alert has been open and is being open again
    } else if (open && prevProps.open) {
      // clear any previous close timer
      if (typeof this.closeTimer !== 'undefined' && this.closeTimer !== null) {
        clearTimeout(this.closeTimer);
      }
      // if the new alert is a success, set a new close timer
      if (alertType === 'success') {
        this.closeTimer = setTimeout(() => {
          this.closeTimer = undefined;
          closeAlert(id || '');
        }, CLOSE_TIME);
      }
      // clear any previous unset timers
      if (typeof this.unsetTimer !== 'undefined' && this.unsetTimer !== null) {
        clearTimeout(this.unsetTimer);
      }
    }
  }

  handleCloseAlert() {
    const { closeAlert, id } = this.props;
    // when the alert is manually closed we clear the close timer and close the alert
    if (typeof this.closeTimer !== 'undefined' && this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
    }
    closeAlert(id || '');
  }

  compareIdWithShowId = (id: string, showId: string | string[]) => {
    if (typeof showId === 'string') {
      return id === showId;
    } else {
      return showId.indexOf(id) !== -1;
    }
  };

  render() {
    const { alertType, open, messageIntl, message, id, showId } = this.props;
    const messageContent = messageIntl ?
      <FormattedMessage id={messageIntl}>{
        txt => <span className={style.message}>{txt}</span>
      }</FormattedMessage> : <span className={style.message}>{message}</span>;
    return (
      <section className={classNames(style.alertWrapper,
        { [style.open]: open && this.compareIdWithShowId(id, showId) })}>
        <div className={classNames(style.alert,
          { [style.success]: alertType === 'success' },
          { [style.error]: alertType === 'error' })}>
          {alertType === 'success' &&
          <div className={style.successIconBg}>
            <i className={icons.iconCheckMedium}/>
          </div>}
          {alertType === 'error' &&
          <div className={style.errorIconBg}>
            <i className={icons.symbolExclamation}/>
          </div>}
          {messageContent}
          <button className={style.close}
                  onClick={_ => this.handleCloseAlert()}>
            <i className={icons.iconCloseLight}/>
          </button>
        </div>
      </section>
    );
  }
}

Alert.propTypes = {
  id: PropTypes.string,
  alertType: PropTypes.string,
  open: PropTypes.bool,
  message: PropTypes.string,
  messageIntl: PropTypes.string,
  unsetAlert: PropTypes.func.isRequired,
  closeAlert: PropTypes.func.isRequired,
};

Alert.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/alert/components/Alert.jsx